import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import MessageCard from '../../../components/card/MessageCard'
import { Card, CardContent, Grid } from '@mui/material'
import Input from '../../../components/forms/Input'
import ColorPicker from '../../../components/forms/ColorPicker'
import i18n from 'simple-react-i18n'
import Select from '../../../components/forms/Select'
import { HORIZON_TYPE_LABEL, MODEL_OBJECTIVES_LABEL, MODEL_TYPES } from '../../../iaeau/constants/IAEauConstants'
import NumberField from '../../../components/forms/NumberField'
import Checkbox from '../../../components/forms/Checkbox'
import IAEauAction from '../../../iaeau/IAEauAction'
import HomeAction from '../../../home/actions/HomeAction'
import { execByType, getStationTitleWithTownName } from '../../../utils/StationUtils'
import { SmallStyledFieldSet, StyledLegend } from '../../../components/StyledElements'
import RadioButtons from '../../../components/forms/RadioButtons'
import SimpleDatePicker from '../../../components/forms/SimpleDatePicker'
import { getLabel } from '../../../utils/StoreUtils'
import { hasValue } from '../../../utils/NumberUtil'
import useActions from '../../../utils/customHook/useActions'
import { useParams } from 'react-router'
import { CardTitle } from 'components/card/NewCard'
import ToastrAction from 'toastr/actions/ToastrAction'
import { AVERAGE, MAX, MIN } from 'utils/constants/MeasureConstants'
import { nbPerPageLabelTiny } from '../../../referencial/constants/ReferencialConstants'
import { CardTable } from '../../../components/datatable/NewTable'
import { getSiteUrl } from '../../../utils/mapUtils/SiteTypes'
import { STATION_NAME_ASSOCIATION, STATION_TYPE_NAME } from '../../constants/StationConstants'
import JobAction from '../../../import/actions/JobAction'
import HydratecParamsPanel from './modelParamsPanel/HydratecParamsPanel'
import { getHour } from '../../../utils/DateUtil'
import { push } from '@lagunovsky/redux-react-router'
import { onChangeHour } from '../../../utils/FormUtils'
import { filter, head, isEmpty, keys, orderBy } from 'lodash'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import PiezometerStationAction from 'station/actions/PiezometerStationAction'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import DataInputs from './DataInputs'

const getStationsByType = (stations, typeName) => orderBy(filter(stations, { typeName }), 'id')

const ARIMAParamsExpert = ({
    modelParams = {},
    setModelParams = () => {},
}) => (
    <>
        <Grid container size={12} justifyContent='center' alignItems='center' spacing={1.5}>
            <Grid size={4}>
                <NumberField
                    value={modelParams.p}
                    title='p'
                    onChange={p => setModelParams({ p })}
                />
            </Grid>
            <Grid size={4}>
                <NumberField
                    value={modelParams.q}
                    title='q'
                    onChange={q => setModelParams({ q })}
                />
            </Grid>
            <Grid size={4}>
                <NumberField
                    value={modelParams.d}
                    title='d'
                    onChange={d => setModelParams({ d })}
                />
            </Grid>
        </Grid>
        <Grid container size={12} justifyContent='center' alignItems='center' spacing={1.5}>
            <Grid size={4}>
                <NumberField
                    value={modelParams.P}
                    title='P'
                    onChange={P => setModelParams({ P })}
                />
            </Grid>
            <Grid size={4}>
                <NumberField
                    value={modelParams.Q}
                    title='Q'
                    onChange={Q => setModelParams({ Q })}
                />
            </Grid>
            <Grid size={4}>
                <NumberField
                    value={modelParams.D}
                    title='D'
                    onChange={D => setModelParams({ D })}
                />
            </Grid>
        </Grid>
    </>
)

ARIMAParamsExpert.propTypes = {
    modelParams: PropTypes.shape({}),
    setModelParams: PropTypes.func,
}

const AUGURESParamsExpert = ({
    modelParams = {},
    setModelParams = () => {},
}) => (
    <Grid size={4}>
        <NumberField
            value={modelParams.average}
            title={i18n.average}
            onChange={average => setModelParams({ average })}
        />
    </Grid>
)

AUGURESParamsExpert.propTypes = {
    modelParams: PropTypes.shape({}),
    setModelParams: PropTypes.func,
}

const ARIMAParams = ({
    modelParams = {},
    setModelParams = () => {},
}) => (
    <>
        <Grid size={4}>
            <Select
                value={modelParams.measureGroupMode}
                options={[
                    { value: MAX, label: 'Mesures Max' },
                    { value: MIN, label: 'Mesures Min' },
                    { value: AVERAGE, label: 'Mesures moyennes' },
                ]}
                label='Regroupement des mesures'
                onChange={measureGroupMode => setModelParams({ measureGroupMode })}
            />
        </Grid>
        <Grid size={4}>
            <Checkbox
                checked={modelParams.area30}
                label='Intervalle de confiance 30 %'
                onChange={area30 => setModelParams({ area30 })}
            />
        </Grid>
        <Grid size={4}>
            <Checkbox
                checked={modelParams.area50}
                label='Intervalle de confiance 50 %'
                onChange={area50 => setModelParams({ area50 })}
            />
        </Grid>
        <Grid size={4}>
            <Checkbox
                checked={modelParams.area90}
                label='Intervalle de confiance 90 %'
                onChange={area90 => setModelParams({ area90 })}
            />
        </Grid>
    </>
)

ARIMAParams.propTypes = {
    modelParams: PropTypes.shape({}),
    setModelParams: PropTypes.func,
}

const SCIKITLEARNGENERICParams = ({
    modelParams = {},
    setModelParams = () => {},
}) => {
    const stationsFormatted = modelParams.stations.map(station => ({
        ...station,
        type: (
            <img
                src={getSiteUrl(STATION_NAME_ASSOCIATION[station.typeName])}
                className='tooltipped'
                data-tooltip={i18n[station.typeName]}
                style={{ maxHeight: '30px', padding: '2px 0' }}
            />
        ),
    }))
    return (
        <>
            <Grid container size={12} columnSpacing={3} rowSpacing={1.5}>
                <Grid>
                    <Checkbox
                        checked={modelParams.area50}
                        label='Intervalle de confiance 50 %'
                        onChange={area50 => setModelParams({ area50 })}
                    />
                </Grid>
                <Grid>
                    <Checkbox
                        checked={modelParams.area60}
                        label='Intervalle de confiance 60 %'
                        onChange={area60 => setModelParams({ area60 })}
                    />
                </Grid>
                <Grid>
                    <Checkbox
                        checked={modelParams.area70}
                        label='Intervalle de confiance 70 %'
                        onChange={area70 => setModelParams({ area70 })}
                    />
                </Grid>
                <Grid>
                    <Checkbox
                        checked={modelParams.area80}
                        label='Intervalle de confiance 80 %'
                        onChange={area80 => setModelParams({ area80 })}
                    />
                </Grid>
                <Grid>
                    <Checkbox
                        checked={modelParams.area90}
                        label='Intervalle de confiance 90 %'
                        onChange={area90 => setModelParams({ area90 })}
                    />
                </Grid>
                <Grid>
                    <Checkbox
                        checked={modelParams.area95}
                        label='Intervalle de confiance 95 %'
                        onChange={area95 => setModelParams({ area95 })}
                    />
                </Grid>
                <Grid>
                    <Checkbox
                        checked={modelParams.area99}
                        label='Intervalle de confiance 99 %'
                        onChange={area99 => setModelParams({ area99 })}
                    />
                </Grid>
            </Grid>
            <Grid container size={12} columnSpacing={3} rowSpacing={1.5}>
                <Grid size={6}>
                    <CardTable
                        title={i18n.selectedStations}

                        rows={stationsFormatted}
                        rowsPerPageOptions={nbPerPageLabelTiny}
                        headers={['type', 'code', 'name']}
                    />
                </Grid>
            </Grid>
        </>
    )
}

SCIKITLEARNGENERICParams.propTypes = {
    modelParams: PropTypes.shape({}),
    setModelParams: PropTypes.func,
}

const TarriBRGMParams = ({
    horizonMode = 'days',
    modelParams = {},
    setModelParams = () => {},
}) => (
    <Grid size={4}>
        <NumberField
            title={`Sensibilité (${HORIZON_TYPE_LABEL[horizonMode]})`}
            value={modelParams.sensibility}
            onChange={sensibility => setModelParams({ sensibility })}
            min={2}
        />
    </Grid>
)

TarriBRGMParams.propTypes = {
    horizonMode: PropTypes.string,
    modelParams: PropTypes.shape({}),
    setModelParams: PropTypes.func,
}

const AUGURESParams = ({
    modelParams = {},
    setModelParams = () => {},
}) => (
    <>
        <Grid size={4}>
            <Select
                value={modelParams.lineToTake}
                options={[
                    { value: 'area', label: 'Intervalle de confiance' },
                    { value: 'average', label: i18n.average },
                    { value: 'median', label: i18n.median },
                    { value: 'years', label: 'Années les plus probables' },
                ]}
                label={i18n.displayMode}
                onChange={lineToTake => setModelParams({ lineToTake })}
            />
        </Grid>
        <Grid size={4}>
            <NumberField
                title='Nb années à sélectionner'
                value={modelParams.nbYearsToTake}
                min={0}
                max={5}
                onChange={nbYearsToTake => setModelParams({ nbYearsToTake })}
                disabled={modelParams.lineToTake !== 'years'}
            />
        </Grid>
    </>
)

AUGURESParams.propTypes = {
    modelParams: PropTypes.shape({}),
    setModelParams: PropTypes.func,
}

const IAEauModelApp = ({
    stationType,
}) => {
    const dispatch = useDispatch()
    const { modelId, id } = useParams()

    const {
        iaeauModels,
        piezometer,
        hydrometricStation,
        jobs,
        citiesIndex,
    } = useSelector(store => ({
        iaeauModels: store.IAEauReducer.iaeauModels,
        piezometer: store.StationReducer.piezometer,
        hydrometricStation: store.HydrometryReducer.hydrometricStation,
        jobs: store.JobReducer.jobs.filter(j => j.jobType?.startsWith('modelCalculationManually') && j.id > 0),
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const [selectedModel, setSelectedModel] = useState(null)
    const [nbTitleClick, setNbTitleClick] = useState(0)
    const changeModel = (changes) => setSelectedModel(prev => ({ ...prev, ...changes }))
    const changeParams = (changes) => setSelectedModel(prev => ({ ...prev, params: JSON.stringify({ ...JSON.parse(prev.params), ...changes }) }))

    useActions(() => {
        return {
            play: () => {
                IAEauAction.launchModel(stationType, parseInt(id), selectedModel).then(() => {
                    dispatch(ToastrAction.success(i18n.jobLaunched))
                    dispatch(push(`/station/${stationType}/${id}/iaeauModels`))
                })
            },
            save: () => {
                // if (selectedModel.calculationMode === 'period' && moment(selectedModel.simulationDate || moment().valueOf()).add(30, 'days').valueOf() < (selectedModel.simulationEndDate || moment().valueOf())) {
                //     dispatch(ToastrAction.error('Le calcul sur une période ne peut pas excéder 30 jours'))
                // } else {
                IAEauAction.updateModel(stationType, parseInt(id), selectedModel).then(() => {
                    dispatch(IAEauAction.getModels(stationType, parseInt(id)))
                })
                // }
            },
        }
    }, [selectedModel])

    const townName = useMemo(() => (stationType === STATION_TYPE_NAME.hydrometry) ?
        citiesIndex[hydrometricStation.townCode]?.name :
        citiesIndex[piezometer.townCode]?.name
    , [citiesIndex, piezometer, hydrometricStation, stationType])

    useEffect(() => {
        if (selectedModel?.idModel) {
            const station = execByType(stationType, {
                piezometry: () => piezometer,
                hydrometry: () => hydrometricStation,
            })
            dispatch(HomeAction.setTitle([{
                title: i18n[stationType],
                href: `${stationType}`,
            }, {
                title: getStationTitleWithTownName(station, townName),
                href: `station/${stationType}/${station.id}`,
            }, {
                title: i18n.models,
                href: `station/${stationType}/${station.id}/iaeauModels`,
            }, {
                title: `${selectedModel.name} [${selectedModel.typeModel}]`,
                href: `station/${stationType}/${station.id}/iaeauModels/${selectedModel.idModel}`,
            }]))
        }
    }, [selectedModel, townName])

    useEffect(() => {
        dispatch(IAEauAction.getModels(stationType, parseInt(id)))
        dispatch(JobAction.fetchJobs())
    }, [dispatch, id, stationType])

    useEffect(() => {
        const parsedModelId = parseInt(modelId, 10)
        const parsedStationId = parseInt(id, 10)
        const stationScopedModels = iaeauModels.filter(m => !m.idStation || parseInt(m.idStation, 10) === parsedStationId)
        const model = stationScopedModels.find(m => parseInt(m.idModel, 10) === parsedModelId)
        setSelectedModel(model)
    }, [iaeauModels, modelId, id])

    const modelParams = useMemo(() => {
        try {
            return JSON.parse(selectedModel?.params || '{}')
        } catch (e) {
            return {}
        }
    }, [selectedModel?.params])

    const modelStations = useMemo(() => {
        if (!isEmpty(modelParams?.stations)) {
            return modelParams.stations
        }
        return id && stationType ? [{ id: parseInt(id, 10), typeName: stationType }] : []
    }, [modelParams?.stations, id, stationType])

    const piezos = useMemo(() => getStationsByType(modelStations, STATION_TYPE_NAME.piezometry), [modelStations])
    const hydros = useMemo(() => getStationsByType(modelStations, STATION_TYPE_NAME.hydrometry), [modelStations])
    const pluvios = useMemo(() => getStationsByType(modelStations, STATION_TYPE_NAME.pluviometry), [modelStations])
    const orderedStations = useMemo(() => [...piezos, ...hydros, ...pluvios], [piezos, hydros, pluvios])
    const stationsKey = useMemo(() => orderedStations.map(station => `${station.id}-${station.typeName}`).join(','), [orderedStations])

    const [stats, setStats] = useState([])
    const [predStats, setPredStats] = useState([])

    useProgressDispatch(() => {
        if (!selectedModel || !stationsKey) {
            return []
        }
        const firstPluvioId = head(pluvios)?.id
        const statsPromises = [
            ...piezos.map(station => PiezometerStationAction.promisePiezoMeasuresStats(station.id).catch(() => [])),
            ...hydros.map(station => HydrometryAction.promiseHydroStats(station.id).catch(() => [])),
            ...pluvios.map(station => PluviometryAction.promisePluviometerMeasuresStats(station.id).catch(() => [])),
        ]
        const predStatsPromise = firstPluvioId
            ? IAEauAction.promisePredStats(STATION_TYPE_NAME.pluviometry, firstPluvioId).then(result => setPredStats(result || [])).catch(() => setPredStats([]))
            : Promise.resolve(setPredStats([]))
        return [
            Promise.all(statsPromises).then(setStats).catch(() => setStats([])),
            predStatsPromise,
        ]
    }, [stationsKey, selectedModel?.idModel])

    if (!selectedModel) {
        return <MessageCard>{i18n.modelNotFound}</MessageCard>
    }
    const objectives = keys(MODEL_OBJECTIVES_LABEL).map(objectiveId => ({ value: objectiveId, label: i18n[MODEL_OBJECTIVES_LABEL[objectiveId]] }))
    const horizonModes = [
        { value: 'hours', label: i18n.hours },
        { value: 'days', label: i18n.days },
        { value: 'weeks', label: i18n.week },
        { value: 'months', label: i18n.month },
    ]
    const lineTypes = [
        { value: 'solid', label: i18n.full },
        { value: 'dashed', label: i18n.dottedLines },
        { value: 'dotted', label: i18n.dotted },
    ]

    return (
        <Grid container rowSpacing={1.5} sx={{ padding: '1rem' }}>
            <Grid size={12}>
                <Card>
                    <CardTitle
                        title={(
                            <span onClick={() => setNbTitleClick(prev => prev + 1)}>
                                {`${selectedModel.name} [${selectedModel.typeModel}]`}
                            </span>
                        )}
                    />
                    <CardContent>
                        <Grid container rowSpacing={1.5}>
                            <Grid size={12}>
                                <SmallStyledFieldSet>
                                    <StyledLegend>Généralités</StyledLegend>
                                    <Grid container spacing={1.5} alignItems='center'>
                                        <Grid size={3}>
                                            <Input
                                                value={selectedModel.name}
                                                onChange={v => changeModel({ name: v })}
                                                title={i18n.modelName}
                                            />
                                        </Grid>
                                        <Grid size={2}>
                                            <Input
                                                value={selectedModel.typeModel}
                                                title={i18n.type}
                                                disabled
                                            />
                                        </Grid>
                                        <Grid size={2}>
                                            <Input
                                                value={selectedModel.tag}
                                                onChange={v => changeModel({ tag: v })}
                                                title={i18n.tag}
                                            />
                                        </Grid>
                                        <Grid size={2}>
                                            <Select
                                                value={selectedModel.objective}
                                                options={objectives}
                                                label={i18n.objective}
                                                disabled
                                            />
                                        </Grid>
                                        <Grid size={2}>
                                            <Input
                                                value={selectedModel.chainCode}
                                                onChange={v => changeModel({ chainCode: v })}
                                                title={i18n.chainCodeForAquasys}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <Select
                                                value={selectedModel.jobId}
                                                options={jobs}
                                                onChange={v => changeModel({ jobId: v })}
                                                label={i18n.job}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <ColorPicker
                                                label={i18n.color}
                                                value={selectedModel.color}
                                                onChange={v => changeModel({ color: v })}
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedModel.color,
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <Select
                                                value={selectedModel.lineType || 'dashed'}
                                                options={lineTypes}
                                                label={i18n.lineType}
                                                onChange={v => changeModel({ lineType: v })}
                                                noNullValue
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <NumberField
                                                title={i18n.lineWidth}
                                                value={selectedModel.lineWidth || 2 }
                                                onChange={v => changeModel({ lineWidth: v })}
                                                min={1}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <NumberField
                                                title={i18n.lineOpacity}
                                                value={hasValue(selectedModel.lineOpacity) ? selectedModel.lineOpacity : 1 }
                                                onChange={v => changeModel({ lineOpacity: v })}
                                                min={0}
                                                max={1}
                                                floatValue
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <Checkbox
                                                checked={selectedModel.displayDashboard}
                                                onChange={v => changeModel({ displayDashboard: v })}
                                                label={i18n.useOnDashboard}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <Checkbox
                                                checked={selectedModel.displaySituation}
                                                onChange={v => changeModel({ displaySituation: v })}
                                                label={i18n.useOnLocationMap}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <Checkbox
                                                checked={selectedModel.displayAlert}
                                                onChange={v => changeModel({ displayAlert: v })}
                                                label={i18n.useOnAlertProcessing}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <Checkbox
                                                checked={selectedModel.displaySuivi}
                                                onChange={v => changeModel({ displaySuivi: v })}
                                                label={i18n.useOnGraphTracking}
                                            />
                                        </Grid>
                                    </Grid>
                                </SmallStyledFieldSet>
                            </Grid>
                            <Grid size={12}>
                                <SmallStyledFieldSet>
                                    <StyledLegend>{i18n.calculsModes}</StyledLegend>
                                    <Grid container spacing={1.5} alignItems='center'>
                                        <Grid alignItems='center' size={12}>
                                            <RadioButtons
                                                elements={[
                                                    { code: 'auto', name: i18n.allDays },
                                                    { code: 'now', name: i18n.today },
                                                    { code: 'date', name: i18n.chooseDate },
                                                    { code: 'period', name: i18n.periode },
                                                    { code: 'lastMeasure', name: i18n.lastMeasure },
                                                ]}
                                                colOption={1.5}
                                                selected={selectedModel.calculationMode || 'now'}
                                                onChange={v => changeModel({ calculationMode: v })}
                                                title={i18n.calculType}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <SimpleDatePicker
                                                label={selectedModel.calculationMode === 'period' ? i18n.startDate : i18n.date}
                                                value={selectedModel.simulationDate}
                                                onChange={date => changeModel({ simulationDate: date })}
                                                disabled={!['period', 'date'].includes(selectedModel.calculationMode ?? 'now')}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <Input title={i18n.hour} value={getHour(selectedModel.simulationDate)}
                                                onChange={v => onChangeHour(v, v2 => changeModel({ simulationDate: v2 }), undefined, selectedModel.simulationDate)}
                                                disabled={!['period', 'date'].includes(selectedModel.calculationMode ?? 'now')}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <SimpleDatePicker
                                                label={i18n.endDate}
                                                value={selectedModel.simulationEndDate}
                                                onChange={date => changeModel({ simulationEndDate: date })}
                                                disabled={selectedModel.calculationMode !== 'period'}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <Input title={i18n.endHour} value={getHour(selectedModel.simulationEndDate)}
                                                onChange={v => onChangeHour(v, v2 => changeModel({ simulationDate: v2 }), undefined, selectedModel.simulationEndDate)}
                                                disabled={selectedModel.calculationMode !== 'period'}
                                            />
                                        </Grid>
                                        <Grid size={4}>
                                            <NumberField
                                                value={selectedModel.horizon}
                                                onChange={v => changeModel({ horizon: v })}
                                                title={`${i18n.horizon} (${getLabel(horizonModes, selectedModel.horizonMode || 'day', 'label', 'value')})`}
                                                disabled
                                            />
                                        </Grid>
                                        <Grid size={4}>
                                            <Select
                                                value={selectedModel.horizonMode || 'days'}
                                                onChange={v => changeModel({ horizonMode: v })}
                                                label={i18n.noTimeHorizon}
                                                options={horizonModes}
                                                disabled
                                            />
                                        </Grid>
                                        <Grid size={4}>
                                            <Select
                                                value={selectedModel.recurPeriod || 'days'}
                                                onChange={v => changeModel({ recurPeriod: v })}
                                                label={i18n.recurrencePeriodicCalculation}
                                                options={horizonModes}
                                            />
                                        </Grid>
                                    </Grid>
                                </SmallStyledFieldSet>
                            </Grid>
                            <Grid size={12}>
                                {[MODEL_TYPES.AUGURES, MODEL_TYPES.ARIMA, MODEL_TYPES.TARRI_BRGM, MODEL_TYPES.SCIKIT_LEARN_GENERIC, MODEL_TYPES.HYDRATEC, MODEL_TYPES.GR4J_MODEL].includes(selectedModel.typeModel) && (
                                    <SmallStyledFieldSet>
                                        <StyledLegend>{i18n.parameters}</StyledLegend>
                                        <Grid container spacing={1.5} alignItems='center'>
                                            {selectedModel.typeModel === MODEL_TYPES.AUGURES && (
                                                <AUGURESParams
                                                    modelParams={JSON.parse(selectedModel.params)}
                                                    setModelParams={changeParams}
                                                />
                                            )}
                                            {selectedModel.typeModel === MODEL_TYPES.ARIMA && (
                                                <ARIMAParams
                                                    modelParams={JSON.parse(selectedModel.params)}
                                                    setModelParams={changeParams}
                                                />
                                            )}
                                            {selectedModel.typeModel === MODEL_TYPES.TARRI_BRGM && (
                                                <TarriBRGMParams
                                                    horizonMode={selectedModel.horizonMode}
                                                    modelParams={JSON.parse(selectedModel.params)}
                                                    setModelParams={changeParams}
                                                />
                                            )}
                                            {(selectedModel.typeModel === MODEL_TYPES.SCIKIT_LEARN_GENERIC || selectedModel.typeModel === MODEL_TYPES.GR4J_MODEL) && (
                                                <SCIKITLEARNGENERICParams
                                                    modelParams={JSON.parse(selectedModel.params)}
                                                    setModelParams={changeParams}
                                                />
                                            )}
                                            {selectedModel.typeModel === MODEL_TYPES.HYDRATEC && (
                                                <HydratecParamsPanel
                                                    model={selectedModel}
                                                    id={id}
                                                />
                                            )}
                                        </Grid>
                                    </SmallStyledFieldSet>
                                )}
                            </Grid>
                            <Grid size={12}>
                                <DataInputs
                                    selectedModel={selectedModel}
                                    stations={orderedStations}
                                    stats={stats}
                                    predStats={predStats}
                                    stationType={stationType}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            {nbTitleClick >= 5 && (
                <Grid size={12}>
                    <Card>
                        <CardTitle title='/!\ Mode Expert /!\' />
                        <CardContent>
                            {(() => {
                                switch (selectedModel.typeModel) {
                                    case MODEL_TYPES.AUGURES:
                                        return (
                                            <AUGURESParamsExpert
                                                modelParams={JSON.parse(selectedModel.params)}
                                                setModelParams={changeParams}
                                            />
                                        )
                                    case MODEL_TYPES.ARIMA:
                                        return (
                                            <ARIMAParamsExpert
                                                modelParams={JSON.parse(selectedModel.params)}
                                                setModelParams={changeParams}
                                            />
                                        )
                                    case MODEL_TYPES.SCIKIT_LEARN_GENERIC:
                                        return (
                                            <Grid container size={12} columnSpacing={3} rowSpacing={1.5}>
                                                <Input
                                                    value={selectedModel.version}
                                                    onChange={v => changeModel({ version: v })}
                                                    title={i18n.version}
                                                />
                                            </Grid>
                                        )
                                    default:
                                        return 'Aucune options en plus :('
                                }
                            })()}
                        </CardContent>
                    </Card>
                </Grid>
            )}
        </Grid>
    )
}

IAEauModelApp.propTypes = {
    params: PropTypes.shape({
        modelId: PropTypes.string,
    }),
    stationType: PropTypes.string,
    id: PropTypes.string,
    modelId: PropTypes.string,
}

export default IAEauModelApp
